import { useCallback, useEffect, useState } from 'react';
import { PAGE_SIZE } from '../common';
import { api, ApiError } from '../../lib/api';
import { useFetchTrigger } from '../../lib/useFetchTrigger';
import type { Owner, OwnerStats } from '../bookings/types';

export type OwnerFilterKey = 'all' | 'regulars' | 'newcomers' | 'boarding';

// Every owner-table column is sortable (parity with the bookings table).
export type OwnerSortKey = 'dog' | 'breed' | 'contact' | 'address' | 'visits' | 'spend' | 'updated' | 'added';
export interface OwnerSortState {
  key: OwnerSortKey;
  dir: 'asc' | 'desc';
}
export const DEFAULT_OWNER_SORT: OwnerSortState = { key: 'added', dir: 'desc' };

export interface OwnersPageFilter {
  q: string | null;
  filter: OwnerFilterKey;
}

// Pure: build the GET /api/owners?mode=page list query for a filter + sort +
// page. A trimmed, non-empty term is sent as `q`; the default `all` chip is
// omitted so the server clause switches off. sort/dir + limit/offset are always
// present. Returns the path relative to the API base. Exported for unit testing.
export function buildOwnersPageQuery(
  filter: OwnersPageFilter,
  sort: OwnerSortState,
  limit: number,
  offset: number,
): string {
  const p = new URLSearchParams();
  p.set('mode', 'page');
  p.set('limit', String(limit));
  p.set('offset', String(offset));
  p.set('sort', sort.key);
  p.set('dir', sort.dir);
  const q = filter.q?.trim();
  if (q) p.set('q', q);
  if (filter.filter !== 'all') p.set('filter', filter.filter);
  return `/owners?${p.toString()}`;
}

export type OwnersLoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message?: string }
  | { phase: 'ready'; owners: Owner[] };

export interface UseOwnersPageResult {
  state: OwnersLoadState;
  stats: OwnerStats | null;
  total: number | null;
  page: number; // 1-based
  setPage: (page: number) => void;
  reload: () => void;
}

// The Dogs & Owners list hook — server-side search/filter + 20-per-page
// pagination (shared Pagination), mirroring useBookings. Each page is a fresh
// fetch at offset (page-1)*PAGE_SIZE; filter/search changes reset to page 1.
// `total` comes back on the first page (offset 0) and is cached across pages.
// The stat strip + chip counts come from GET /api/owners/stats — page-invariant
// (over ALL owners, AC4), so they're fetched once per result-set, not per page,
// and refreshed on reload(). A request-gen guard discards superseded responses.
export function useOwnersPage(filter: OwnersPageFilter, sort: OwnerSortState): UseOwnersPageResult {
  const [state, setState] = useState<OwnersLoadState>({ phase: 'loading' });
  const [stats, setStats] = useState<OwnerStats | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  // Two channels of the shared SWR trigger core (lib/useFetchTrigger) — the
  // list and the stat strip fetch on different deps, so each needs its own
  // trigger bookkeeping (prev nonce/tick, hasContent, generation). Both see the
  // same activation+mutation tick; reload() bumps both, so a post-write refetch
  // re-reads list AND strip with the browser cache bypassed (read-cache-strategy
  // AC3/AC4 — W4 stales AP4 + AP6). Page/filter changes re-read cacheable.
  const listTrigger = useFetchTrigger();
  const statsTrigger = useFetchTrigger();

  // Reset to page 1 whenever the filter, search, or sort changes.
  useEffect(() => {
    setPage((p) => (p === 1 ? p : 1));
  }, [filter.q, filter.filter, sort.key, sort.dir]);

  // The page list fetch.
  useEffect(() => {
    const run = listTrigger.begin();
    if (run.plan.showLoading) setState({ phase: 'loading' });
    api<Owner[]>(buildOwnersPageQuery(filter, sort, PAGE_SIZE, (page - 1) * PAGE_SIZE), run.init)
      .then((body) => {
        if (!run.isCurrent()) return; // superseded by a newer page/filter/return
        if (body.page?.total != null) setTotal(body.page.total);
        run.markContent();
        setState({ phase: 'ready', owners: body.data ?? [] });
      })
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) return; // already redirected
        if (run.isCurrent()) {
          // A failed silent revalidation leaves the last-known rows in place.
          if (run.isRevalidate && run.hasContent) return;
          setState({ phase: 'error', message: e instanceof Error ? e.message || undefined : undefined });
        }
      });
    return run.cancel;
  }, [filter.q, filter.filter, sort.key, sort.dir, page, listTrigger.nonce, listTrigger.tick]);

  // The stat strip + chip counts — over ALL owners, independent of pagination
  // (AC4). Refetched when the result-set could have changed (a reload) or on a
  // return to the page (a revalidation), not on page nav. A revalidation keeps
  // the current numbers on screen and swaps them in place when fresh data lands.
  useEffect(() => {
    const run = statsTrigger.begin();
    api<OwnerStats>('/owners/stats', run.init)
      .then((body) => {
        if (!run.isCurrent()) return;
        run.markContent();
        setStats(body.data ?? null);
      })
      .catch(() => {
        /* 401 redirected inside the client; otherwise the strip stays blank and
           the list still works */
      });
    return run.cancel;
  }, [statsTrigger.nonce, statsTrigger.tick]);

  const reload = useCallback(() => {
    listTrigger.reload();
    statsTrigger.reload();
  }, [listTrigger.reload, statsTrigger.reload]);
  return { state, stats, total, page, setPage, reload };
}
